/**
 * Versioned manifest describing the client-side ASR runtime assets and where they
 * are hosted (TT-825). The manifest is the single source of truth for which model
 * and ONNX Runtime files the cache layer pre-caches, validates, and serves, and it
 * doubles as the update/rollback switch: bumping `VERSION` (and republishing assets
 * under a new immutable CDN path) activates a new model, while reverting `VERSION`
 * restores a previous known-good runtime.
 *
 * Asset base resolution: all URLs derive from a single constant (`ASSET_BASE`).
 * It is hardcoded to the same-origin CloudFront path because the asset path is always
 * tied to the current deployment. For local development before TT-824's CloudFront
 * behavior is live, temporarily point `ASSET_BASE` at a static server hosting the
 * PR #5731 files or the `asr-cdn-handoff/` folder (see ./README.md).
 *
 * Contract notes (from PR feat/nemo-asr-port):
 * - `createAsr()` fetches `configUrl` and `vocabUrl` via app-level fetch, and hands
 *   `modelUrl` + `wasmDir` to ONNX Runtime, which fetches the model and
 *   `ort-wasm-simd-threaded.jsep.*` files itself. Those ORT-internal fetches are why
 *   caching is done through a service worker scoped to the asset base (see asrAssetCache).
 * - `ort.mjs` is cached here too (as its precompressed `ort.mjs.br` sibling) because
 *   TT-826 dynamically imports it from the same CDN versioned vendor path rather
 *   than bundling it into the app build.
 * - The `.manifest.json` companion file is not consumed by the engine; it carries
 *   per-artifact SHA-256 / size used here only for optional integrity validation.
 */

/**
 * Same-origin base path for ASR runtime files, served by the deployment's CloudFront
 * distribution (TT-824). Hardcoded because the path is always tied to the current
 * deployment; no trailing slash. For local development, temporarily replace this with
 * a static-server URL (e.g. 'http://localhost:8088/asr-assets/v1').
 */
const ASSET_BASE = '/asr-assets/v1';

/** Directory prefix passed to ONNX Runtime (`ort.env.wasm.wasmPaths`); must end in a slash. */
const WASM_DIR = `${ASSET_BASE}/vendor/`;

/** Shared filename stem for the en-us FastConformer model bundle. */
const MODEL_STEM = 'amira-asr_en-us_fastconformer-ft38';

/**
 * Model version identifier. Kept in lock-step with the CDN path segment in
 * `ASSET_BASE` (e.g. `.../v1`). Republishing a new model means publishing to a
 * new immutable path and bumping this value plus the cache name.
 */
export const ASR_ASSET_VERSION = 'v1';

/** Cache Storage bucket name; versioned so a new model deploy uses a fresh cache. */
export const ASR_CACHE_NAME = `asr-model-${ASR_ASSET_VERSION}`;

/**
 * Resolved manifest consumed by asrAssetCache and the createAsr() call site.
 * All URLs are absolute-from-origin (or fully-qualified when the base is overridden
 * to an external dev host).
 */
export const ASR_ASSET_MANIFEST = Object.freeze({
  version: ASR_ASSET_VERSION,
  cacheName: ASR_CACHE_NAME,
  assetBase: ASSET_BASE,
  modelUrl: `${ASSET_BASE}/${MODEL_STEM}_int8.onnx`,
  vocabUrl: `${ASSET_BASE}/${MODEL_STEM}.vocab.txt`,
  configUrl: `${ASSET_BASE}/${MODEL_STEM}.preprocess.json`,
  modelManifestUrl: `${ASSET_BASE}/${MODEL_STEM}.manifest.json`,
  wasmDir: WASM_DIR,
  /**
   * Exact files the cache layer must fetch and validate before local ASR is
   * considered ready. The ORT runtime resolves every vendor file relative to
   * `wasmDir`, and TT-826 dynamically imports `ort.mjs.br` from the same versioned path.
   *
   * `approxBytes` is the DECODED size of each asset (even for `.br` entries — the
   * progress meter counts transparently-decompressed body bytes, so the weights
   * must be in the same units). It is used only to weight the download-progress
   * bar so the bar advances smoothly across the large files; exact byte accuracy
   * is not required and a wrong value never affects correctness (validation is
   * separate).
   *
   * `.br` adoption: only `ort.mjs` points at its precompressed sibling — it is the
   * one vendor file WE fetch (dynamic import via resolveOrtModuleUrl, which must
   * match this cache key). The `jsep.*` files stay on plain URLs: ORT fetches them
   * itself under fixed filenames relative to `wasmPaths`, so opting them into `.br`
   * would need a rewrite rule in public/asr-sw.js. Decided against — the .mjs is
   * ~46 KB, and the .wasm already hard-requires its exact Content-Type for
   * instantiateStreaming. Revisit if ORT exposes configurable artifact URLs.
   */
  expectedFiles: Object.freeze([
    { url: `${ASSET_BASE}/${MODEL_STEM}.manifest.json`, approxBytes: 4829 },
    { url: `${ASSET_BASE}/${MODEL_STEM}_int8.onnx`, approxBytes: 25916049 },
    { url: `${ASSET_BASE}/${MODEL_STEM}.vocab.txt`, approxBytes: 7589 },
    { url: `${ASSET_BASE}/${MODEL_STEM}.preprocess.json`, approxBytes: 259 },
    { url: `${WASM_DIR}ort.mjs.br`, approxBytes: 837734 },
    { url: `${WASM_DIR}ort-wasm-simd-threaded.jsep.wasm`, approxBytes: 24911187 },
    { url: `${WASM_DIR}ort-wasm-simd-threaded.jsep.mjs`, approxBytes: 46595 },
  ]),
});

/**
 * Builds the option object expected by the ASR package's `createAsr()` so the
 * engine resolves every model-contract file from the same hosted/cache-managed base
 * as the pre-cache step. `backend` and `strideMs` are left to the engine defaults
 * and tuned by the wiring/benchmark tickets (TT-828 / TT-829).
 * @returns {{modelUrl: string, vocabUrl: string, configUrl: string, wasmDir: string}}
 */
export function getCreateAsrAssetOptions() {
  return {
    modelUrl: ASR_ASSET_MANIFEST.modelUrl,
    vocabUrl: ASR_ASSET_MANIFEST.vocabUrl,
    configUrl: ASR_ASSET_MANIFEST.configUrl,
    wasmDir: ASR_ASSET_MANIFEST.wasmDir,
  };
}
